import type { HandlingUnitHashMap, PackingListRow } from "@RequestFormV2/sections/PackingList/types";
import { isContainerRow } from "@RequestFormV2/sections/PackingList/utils/packingListSplit";
import {
  type PackingListFieldRulesContext,
  isAdditionalTextRequiredForRow,
  isNmfcFieldRequiredForRow,
} from "@RequestFormV2/sections/PackingList/utils/packingListWorkflowFlags";
import { isEmptyValue } from "@RequestFormV2/validation/utils/validationMessages";

export type { PackingListFieldRulesContext };

function isMandatory(setting: string | undefined) {
  return setting === "mandatory";
}

export function isPackingListFieldRequired(
  row: PackingListRow,
  field: "dimensions" | "weight" | "units" | "nmfc" | "additional_field",
  handlingUnitHashMap: HandlingUnitHashMap,
  rulesContext: PackingListFieldRulesContext = {},
): boolean {
  if (isContainerRow(row)) {
    return field === "units";
  }

  const key = row.handling_unit?.toLowerCase();
  if (!key) return false;

  const settings = handlingUnitHashMap[key];
  if (!settings) return false;

  if (field === "dimensions") return isMandatory(settings.l_w_h);
  if (field === "weight") return isMandatory(settings.weight);
  if (field === "units") return isMandatory(settings.quantity);
  if (field === "nmfc") {
    return isNmfcFieldRequiredForRow(row, handlingUnitHashMap, rulesContext.nmfcNumberEnabled);
  }
  if (field === "additional_field") {
    return isAdditionalTextRequiredForRow(row, handlingUnitHashMap);
  }
  return false;
}

export function validatePackingListRow(
  row: PackingListRow,
  index: number,
  handlingUnitHashMap: HandlingUnitHashMap,
  rulesContext: PackingListFieldRulesContext = {},
): string[] {
  const errorPaths: string[] = [];

  if (isContainerRow(row)) {
    if (isEmptyValue(row.container_type)) {
      errorPaths.push(`packing_list.${index}.container_type`);
    }
    if (isPackingListFieldRequired(row, "units", handlingUnitHashMap, rulesContext) && isEmptyValue(row.units)) {
      errorPaths.push(`packing_list.${index}.units`);
    }
    return errorPaths;
  }

  if (isEmptyValue(row.handling_unit)) {
    errorPaths.push(`packing_list.${index}.handling_unit`);
    return errorPaths;
  }

  if (isPackingListFieldRequired(row, "units", handlingUnitHashMap, rulesContext) && isEmptyValue(row.units)) {
    errorPaths.push(`packing_list.${index}.units`);
  }

  if (isPackingListFieldRequired(row, "weight", handlingUnitHashMap, rulesContext) && isEmptyValue(row.weight_piece)) {
    errorPaths.push(`packing_list.${index}.weight_piece`);
  }

  if (isPackingListFieldRequired(row, "nmfc", handlingUnitHashMap, rulesContext) && isEmptyValue(row.nmfc_class)) {
    errorPaths.push(`packing_list.${index}.nmfc_class`);
  }

  if (
    isPackingListFieldRequired(row, "additional_field", handlingUnitHashMap, rulesContext) &&
    isEmptyValue(row.additional_field)
  ) {
    errorPaths.push(`packing_list.${index}.additional_field`);
  }

  if (isPackingListFieldRequired(row, "dimensions", handlingUnitHashMap, rulesContext)) {
    (["length", "width", "height"] as const).forEach((dim) => {
      if (isEmptyValue(row[dim])) {
        errorPaths.push(`packing_list.${index}.${dim}`);
      }
    });
  }

  // Custom HUs with LWH = CBM Input require scope + value (matches backend validate_good_items_data_for_qr).
  const settings = handlingUnitHashMap[row.handling_unit.toLowerCase()];
  if (settings?.l_w_h_cbm) {
    if (isEmptyValue(row.cbm_scope)) {
      errorPaths.push(`packing_list.${index}.cbm_scope`);
    }
    const cbmValueField = row.cbm_scope === "total" ? "l_w_h_cbm_total" : "l_w_h_cbm_piece";
    // Backend treats 0 as missing (not inputted_cbm) and returns inputted_cbm_N, which maps to no field.
    if (!(Number(row[cbmValueField]) > 0)) {
      errorPaths.push(`packing_list.${index}.${cbmValueField}`);
    }
  }

  return errorPaths;
}
